import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../services/api';

const fmt = (n: number | string) =>
  `LKR ${Number(n).toLocaleString('en-LK', { minimumFractionDigits: 2 })}`;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

type BudgetRow = {
  account_id: number;
  account_code: string;
  account_name: string;
  account_type: string;
  budget_id?: number;
  budgeted_amount: number;
  actual_amount: number;
  variance: number;
  variance_pct: number;
};

const Budgets: React.FC = () => {
  const navigate = useNavigate();
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState<string>(''); // '' = full year
  const [rows, setRows] = useState<BudgetRow[]>([]);
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const yearNums: string[] = [];
  const curYear = new Date().getFullYear();
  for (let y = curYear + 1; y >= curYear - 4; y--) yearNums.push(String(y));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { fiscal_year: year };
      if (month) params.period_month = month;
      const res = await api.get('/accounting/budgets', { params });
      setRows(res.data.data || []);
      setEdits({});
    } catch {
      /* ignore */
    }
    setLoading(false);
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const bulk = Object.entries(edits).map(([account_id_str, amount]) => ({
        account_id: parseInt(account_id_str),
        fiscal_year: parseInt(year),
        period_month: month ? parseInt(month) : null,
        budgeted_amount: parseFloat(amount) || 0,
      }));
      if (!bulk.length) {
        setSaving(false);
        return;
      }
      await api.post('/accounting/budgets/bulk', { budgets: bulk });
      setSuccess('Budgets saved');
      load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Save error');
    }
    setSaving(false);
  };

  const getValue = (row: BudgetRow): string =>
    edits[row.account_id] !== undefined ? edits[row.account_id] : String(row.budgeted_amount || '');

  const totalBudget = rows.reduce((s, r) => s + (Number(edits[r.account_id] ?? r.budgeted_amount) || 0), 0);
  const totalActual = rows.reduce((s, r) => s + r.actual_amount, 0);
  const totalVar = totalActual - totalBudget;

  // Group rows by account_type
  const groups: Record<string, BudgetRow[]> = {};
  rows.forEach((r) => {
    const g = r.account_type || 'Other';
    if (!groups[g]) groups[g] = [];
    groups[g].push(r);
  });
  const groupOrder = ['revenue', 'expense', 'other'];
  const sortedGroups = Object.keys(groups).sort(
    (a, b) => groupOrder.indexOf(a.toLowerCase()) - groupOrder.indexOf(b.toLowerCase())
  );

  const varChip = (v: number, type: string) => {
    if (!v && v !== 0) return null;
    const isExpense = type?.toLowerCase().includes('expense') || type?.toLowerCase() === 'expense';
    const isGood = isExpense ? v < 0 : v > 0;
    const color = v === 0 ? '#999' : isGood ? '#2e7d32' : '#c62828';
    return (
      <VarianceChip color={color}>
        {v > 0 ? '▲' : v < 0 ? '▼' : '—'} {fmt(Math.abs(v))}
      </VarianceChip>
    );
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/accounting')}>← Back</BackButton>
        <div>
          <h1>Budgets & Actuals</h1>
          <Subtitle>Set P&L account budgets and compare against actual journal entry totals</Subtitle>
        </div>
      </Header>

      {/* Summary Cards */}
      <SummaryGrid>
        <SummaryCard>
          <SummaryLabel>Total Budget</SummaryLabel>
          <SummaryValue>{fmt(totalBudget)}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Total Actual</SummaryLabel>
          <SummaryValue>{fmt(totalActual)}</SummaryValue>
        </SummaryCard>
        <SummaryCard highlight={totalVar >= 0}>
          <SummaryLabel>Variance</SummaryLabel>
          <SummaryValue>{totalVar >= 0 ? '+' : ''}{fmt(totalVar)}</SummaryValue>
        </SummaryCard>
      </SummaryGrid>

      {/* Filter Bar */}
      <FilterCard>
        <FilterRow>
          <SelectWrapper>
            <Label>Fiscal Year</Label>
            <Select value={year} onChange={(e) => setYear(e.target.value)}>
              {yearNums.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Select>
          </SelectWrapper>

          <SelectWrapper>
            <Label>Period</Label>
            <Select value={month} onChange={(e) => setMonth(e.target.value)}>
              <option value="">Full Year</option>
              {MONTHS.map((m, i) => (
                <option key={i + 1} value={String(i + 1)}>
                  {m}
                </option>
              ))}
            </Select>
          </SelectWrapper>

          <SaveButton onClick={handleSave} disabled={saving || Object.keys(edits).length === 0}>
            {saving ? 'Saving...' : `Save Changes (${Object.keys(edits).length})`}
          </SaveButton>
        </FilterRow>
      </FilterCard>

      {error && <ErrorAlert>{error}</ErrorAlert>}
      {success && <SuccessAlert>{success}</SuccessAlert>}

      <InfoBanner>
        💡 Enter budgeted amounts in the "Budget" column. Actuals are automatically calculated from posted journal entries.
      </InfoBanner>

      {loading ? (
        <EmptyState>Loading...</EmptyState>
      ) : rows.length === 0 ? (
        <EmptyState>
          <EmptyIcon>📊</EmptyIcon>
          <p>No budget data available for the selected period</p>
        </EmptyState>
      ) : (
        <TableCard>
          <Table>
            <thead>
              <TableHeaderRow>
                <Th>Code</Th>
                <Th>Account</Th>
                <Th>Type</Th>
                <Th style={{ textAlign: 'right' }}>Budget</Th>
                <Th style={{ textAlign: 'right' }}>Actual</Th>
                <Th style={{ textAlign: 'right' }}>Variance</Th>
              </TableHeaderRow>
            </thead>
            <tbody>
              {sortedGroups.map((group) => (
                <React.Fragment key={group}>
                  <GroupHeaderRow>
                    <Td colSpan={6}>
                      <GroupTitle>{group.toUpperCase()}</GroupTitle>
                    </Td>
                  </GroupHeaderRow>
                  {groups[group].map((row) => (
                    <TableRow key={row.account_id}>
                      <Td style={{ fontFamily: 'monospace', fontSize: '12px', color: '#666' }}>
                        {row.account_code}
                      </Td>
                      <Td>{row.account_name}</Td>
                      <Td>
                        <TypeBadge>{row.account_type}</TypeBadge>
                      </Td>
                      <Td style={{ textAlign: 'right' }}>
                        <BudgetInput
                          type="number"
                          step="0.01"
                          value={getValue(row)}
                          onChange={(e) => {
                            const newEdits = { ...edits };
                            newEdits[row.account_id] = e.target.value;
                            setEdits(newEdits);
                            setSuccess('');
                          }}
                        />
                      </Td>
                      <Td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                        {fmt(row.actual_amount)}
                      </Td>
                      <Td style={{ textAlign: 'right' }}>{varChip(row.variance, row.account_type)}</Td>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </TableCard>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;

  h1 {
    font-size: 28px;
    color: #333;
    margin-bottom: 5px;
  }
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 14px;
`;

const BackButton = styled.button`
  background: white;
  border: 1px solid #ddd;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #667eea;
    color: #667eea;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div<{ highlight?: boolean }>`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-left: 4px solid ${props => (props.highlight ? '#2e7d32' : '#667eea')};
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #333;
  font-family: monospace;
`;

const FilterCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 15px;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #555;
`;

const Select = styled.select`
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const SaveButton = styled.button`
  padding: 10px 24px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #5568d3;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorAlert = styled.div`
  background: #fee;
  color: #c00;
  padding: 15px 20px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #fcc;
`;

const SuccessAlert = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 15px 20px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #c3e6cb;
`;

const InfoBanner = styled.div`
  background: #e3f2fd;
  color: #1565c0;
  padding: 15px 20px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #bbdefb;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #666;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  opacity: 0.3;
  margin-bottom: 20px;
`;

const TableCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeaderRow = styled.tr`
  background: #f5f5f5;
`;

const Th = styled.th`
  padding: 15px 20px;
  text-align: left;
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
  border-bottom: 2px solid #ddd;
`;

const GroupHeaderRow = styled.tr`
  background: #fafafa;
`;

const TableRow = styled.tr`
  &:hover {
    background: #f9f9f9;
  }
`;

const Td = styled.td`
  padding: 12px 20px;
  border-bottom: 1px solid #eee;
`;

const GroupTitle = styled.div`
  font-weight: 700;
  font-size: 11px;
  color: #999;
  padding: 8px 0;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: #e3f2fd;
  color: #1565c0;
`;

const BudgetInput = styled.input`
  width: 120px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  font-family: monospace;
  text-align: right;

  &:focus {
    outline: none;
    border-color: #667eea;
    background: #f0f4ff;
  }
`;

const VarianceChip = styled.span<{ color: string }>`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  font-family: monospace;
  background: ${props => props.color}15;
  color: ${props => props.color};
`;

export default Budgets;
